/**
 * PaymentPlanBuilder · iter303ak-fix41 — Overdue · T40 · Action 10
 * ────────────────────────────────────────────────────────────────
 * Full negotiation UI:
 *  • Job details recap (unpaid children summary)
 *  • Mode picker: Lump Sum vs Deposit + Instalments
 *  • Instalments: deposit%, frequency (weekly/fortnightly/monthly), count
 *  • Auto-computed schedule + cost of credit (interest at configurable %)
 *  • Missed-payment fee (default $50)
 *  • Direct Debit authority capture (bank / account name / account #)
 *  • Disclaimer + client-acknowledged tick
 *
 * Persists to parent.payment_plan.* via the standard write_to bubble.
 * Feeds the T40 A20 Payment Plan Confirmation letter.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, DollarSign, Calendar, CheckCircle2, FileText, CreditCard } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const FREQ_META = {
  weekly:      { label: 'Weekly',      days: 7,  perYear: 52 },
  fortnightly: { label: 'Fortnightly', days: 14, perYear: 26 },
  monthly:     { label: 'Monthly',     days: 30, perYear: 12 },
};

const addDaysISO = (iso, days) => {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function PaymentPlanBuilder({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parent = useMemo(() => (capturedData?.appraisal_record || capturedData?.appraisal || {}), [capturedData]);
  const payments = parent.invoiced_payments || {};

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const unpaidChildren = acceptedChildren.filter(c => {
    const s = payments[c.child_id]?.status;
    return s !== 'paid' && s !== 'written_off';
  });

  const outstandingTotal = useMemo(
    () => unpaidChildren.reduce((a, c) => a + Number(payments[c.child_id]?.remaining || 0), 0),
    [unpaidChildren, payments]
  );

  // ── Local state ─────────────────────────────────────────────
  const [mode, setMode]                       = useState(value?.mode || 'instalments'); // 'lump_sum' | 'instalments'
  const [lumpSumDate, setLumpSumDate]         = useState(value?.first_payment_date || addDaysISO(null, 7));
  const [depositAmount, setDepositAmount]     = useState(value?.deposit_amount ?? 0);
  const [frequency, setFrequency]             = useState(value?.frequency || 'weekly');
  const [instalmentCount, setInstalmentCount] = useState(value?.instalment_count || 8);
  const [firstDate, setFirstDate]             = useState(value?.first_payment_date || addDaysISO(null, 7));
  const [interestPct, setInterestPct]         = useState(value?.interest_rate_pct ?? 10);
  const [missedFee, setMissedFee]             = useState(value?.missed_payment_fee ?? 50);

  // ── Direct Debit + Disclaimer ───────────────────────────────
  const [ddBank, setDdBank]           = useState(value?.dd_bank || '');
  const [ddAcctName, setDdAcctName]   = useState(value?.dd_account_name || '');
  const [ddAcctNumber, setDdAcctNumber] = useState(value?.dd_account_number || '');
  const [disclaimerAck, setDisclaimerAck] = useState(!!value?.disclaimer_ack);

  // ── Derived: schedule + cost of credit ──────────────────────
  const derived = useMemo(() => {
    const base = round2(Math.max(0, outstandingTotal - Number(depositAmount || 0)));
    if (mode === 'lump_sum') {
      return {
        base_amount: outstandingTotal,
        deposit: 0,
        principal_after_deposit: outstandingTotal,
        instalment_amount: outstandingTotal,
        interest_total: 0,
        total_repayable: outstandingTotal,
        schedule: [{ date: lumpSumDate, amount: outstandingTotal }],
        final_date: lumpSumDate,
      };
    }
    const meta = FREQ_META[frequency] || FREQ_META.weekly;
    // Simple interest for the plan duration (approx years):
    const years = (Number(instalmentCount || 0) * meta.days) / 365;
    const interest = round2(base * (Number(interestPct || 0) / 100) * years);
    const totalRepayable = round2(base + interest);
    const perInstalment = instalmentCount > 0 ? round2(totalRepayable / instalmentCount) : 0;
    const schedule = [];
    let cursor = firstDate;
    for (let i = 0; i < instalmentCount; i++) {
      schedule.push({ date: cursor, amount: perInstalment });
      cursor = addDaysISO(cursor, meta.days);
    }
    // Adjust last row so it sums exactly to totalRepayable
    if (schedule.length) {
      const sumOthers = schedule.slice(0, -1).reduce((a, r) => a + r.amount, 0);
      schedule[schedule.length - 1].amount = round2(totalRepayable - sumOthers);
    }
    return {
      base_amount: outstandingTotal,
      deposit: Number(depositAmount || 0),
      principal_after_deposit: base,
      instalment_amount: perInstalment,
      interest_total: interest,
      total_repayable: round2(totalRepayable + Number(depositAmount || 0)),
      schedule,
      final_date: schedule.length ? schedule[schedule.length - 1].date : firstDate,
    };
  }, [mode, outstandingTotal, depositAmount, frequency, instalmentCount, firstDate, interestPct, lumpSumDate]);

  // ── Bubble merged state up ──────────────────────────────────
  useEffect(() => {
    if (!onChange) return;
    onChange({
      mode,
      base_amount: derived.base_amount,
      deposit_amount: derived.deposit,
      principal_after_deposit: derived.principal_after_deposit,
      frequency: mode === 'lump_sum' ? 'lump_sum' : frequency,
      instalment_count: mode === 'lump_sum' ? 1 : Number(instalmentCount || 0),
      instalment_amount: derived.instalment_amount,
      first_payment_date: mode === 'lump_sum' ? lumpSumDate : firstDate,
      final_payment_date: derived.final_date,
      interest_rate_pct: Number(interestPct || 0),
      cost_of_credit: derived.interest_total,
      total_repayable: derived.total_repayable,
      missed_payment_fee: Number(missedFee || 0),
      schedule: derived.schedule,
      dd_bank: ddBank,
      dd_account_name: ddAcctName,
      dd_account_number: ddAcctNumber,
      disclaimer_ack: disclaimerAck,
      agreed_at: new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, derived, frequency, instalmentCount, firstDate, lumpSumDate, interestPct, missedFee, ddBank, ddAcctName, ddAcctNumber, disclaimerAck]);

  const canContinue = mode === 'lump_sum'
    ? !!lumpSumDate && disclaimerAck
    : (instalmentCount > 0 && !!firstDate && !!ddBank && !!ddAcctName && !!ddAcctNumber && disclaimerAck);

  return (
    <div className="space-y-4" data-testid="wf-payment-plan-builder">
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-900 uppercase tracking-widest">Payment Plan Builder</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Build the agreed plan · pick Lump Sum or Deposit+Instalments · capture DD authority · tick disclaimer. The confirmation letter is drafted at the next action.'}
            </div>
          </div>
        </div>
      </div>

      {/* Job recap */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-widest text-slate-600">
          Outstanding · {unpaidChildren.length} unpaid child{unpaidChildren.length === 1 ? '' : 'ren'} · Total <b className="font-mono text-rose-700">${fmt(outstandingTotal)}</b>
        </div>
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {unpaidChildren.map(c => {
            const pb = payments[c.child_id] || {};
            return (
              <div key={c.child_id} className="text-xs p-2 border border-slate-200 rounded" data-testid={`wf-plan-child-${c.child_id}`}>
                <div className="font-semibold text-slate-800">
                  {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
                  {c.appraisal_no && <span className="ml-2 text-[9px] font-bold uppercase text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1 py-0.5">A{c.appraisal_no}</span>}
                </div>
                <div className="text-slate-600 mt-0.5">Remaining <b className="font-mono text-rose-700">${fmt(pb.remaining)}</b> of <b className="font-mono">${fmt(pb.due_amount)}</b></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mode picker */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Plan Mode</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <button onClick={() => setMode('lump_sum')} data-testid="wf-plan-mode-lump"
            className={`text-left p-3 border-2 rounded-lg transition-all ${mode === 'lump_sum' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <div className={`font-semibold text-sm ${mode === 'lump_sum' ? 'text-emerald-900' : 'text-slate-800'}`}>
              <DollarSign size={14} className="inline mr-1" /> Lump Sum
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Client pays the full remaining balance on an agreed date.</div>
          </button>
          <button onClick={() => setMode('instalments')} data-testid="wf-plan-mode-instal"
            className={`text-left p-3 border-2 rounded-lg transition-all ${mode === 'instalments' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <div className={`font-semibold text-sm ${mode === 'instalments' ? 'text-amber-900' : 'text-slate-800'}`}>
              <Calendar size={14} className="inline mr-1" /> Deposit + Instalments
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Optional deposit up-front, then weekly / fortnightly / monthly payments.</div>
          </button>
        </div>
      </div>

      {/* Lump Sum config */}
      {mode === 'lump_sum' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-emerald-200 bg-emerald-50/40 rounded-lg">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Agreed payment date</label>
            <input type="date" value={lumpSumDate} onChange={(e) => setLumpSumDate(e.target.value)}
              data-testid="wf-plan-lump-date"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
          </div>
          <div className="flex flex-col justify-center">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Amount due on that date</div>
            <div className="text-lg font-bold text-emerald-800 font-mono" data-testid="wf-plan-lump-amount">${fmt(outstandingTotal)}</div>
          </div>
        </div>
      )}

      {/* Instalments config */}
      {mode === 'instalments' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border border-amber-200 bg-amber-50/40 rounded-lg">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Up-front Deposit ($)</label>
              <input type="number" min="0" step="0.01" value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                data-testid="wf-plan-deposit"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Frequency</label>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
                data-testid="wf-plan-frequency"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded">
                {Object.entries(FREQ_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5"># Instalments</label>
              <input type="number" min="1" max="52" step="1" value={instalmentCount}
                onChange={(e) => setInstalmentCount(Number(e.target.value))}
                data-testid="wf-plan-count"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">First payment date</label>
              <input type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)}
                data-testid="wf-plan-first-date"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border border-slate-200 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Interest rate (% p.a.)</label>
              <input type="number" min="0" max="30" step="0.1" value={interestPct}
                onChange={(e) => setInterestPct(Number(e.target.value))}
                data-testid="wf-plan-interest"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Missed payment fee ($)</label>
              <input type="number" min="0" step="1" value={missedFee}
                onChange={(e) => setMissedFee(Number(e.target.value))}
                data-testid="wf-plan-missed-fee"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Cost of credit</div>
              <div className="text-lg font-bold text-slate-800 font-mono" data-testid="wf-plan-cost-of-credit">${fmt(derived.interest_total)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Total repayable</div>
              <div className="text-lg font-bold text-amber-800 font-mono" data-testid="wf-plan-total-repayable">${fmt(derived.total_repayable)}</div>
            </div>
          </div>

          {/* Schedule table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 text-xs font-bold uppercase tracking-widest text-slate-700">
              Instalment Schedule · {derived.schedule.length} payment{derived.schedule.length === 1 ? '' : 's'} · <b>${fmt(derived.instalment_amount)}</b> each
            </div>
            <div className="max-h-60 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 border-b border-slate-200">#</th>
                    <th className="text-left px-3 py-1.5 border-b border-slate-200">Due Date</th>
                    <th className="text-right px-3 py-1.5 border-b border-slate-200">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {derived.schedule.map((r, i) => (
                    <tr key={i} className="odd:bg-white even:bg-slate-50" data-testid={`wf-plan-sched-${i}`}>
                      <td className="px-3 py-1 border-b border-slate-100 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-1 border-b border-slate-100 font-mono text-slate-800">{r.date}</td>
                      <td className="px-3 py-1 border-b border-slate-100 font-mono text-right text-slate-800">${fmt(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* DD Authority */}
          <div className="p-3 border-2 border-indigo-200 bg-indigo-50/40 rounded-lg space-y-2" data-testid="wf-plan-dd-block">
            <div className="text-xs font-bold uppercase tracking-widest text-indigo-900 flex items-center gap-1">
              <CreditCard size={14} /> Direct Debit Authority — receive from client
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Bank</label>
                <input type="text" value={ddBank} onChange={(e) => setDdBank(e.target.value)}
                  placeholder="e.g. Westpac, ANZ, ASB"
                  data-testid="wf-plan-dd-bank"
                  className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Account name</label>
                <input type="text" value={ddAcctName} onChange={(e) => setDdAcctName(e.target.value)}
                  placeholder="Name on account"
                  data-testid="wf-plan-dd-name"
                  className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Account number</label>
                <input type="text" value={ddAcctNumber} onChange={(e) => setDdAcctNumber(e.target.value)}
                  placeholder="XX-XXXX-XXXXXXX-XX"
                  data-testid="wf-plan-dd-number"
                  className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div className="p-3 border-2 border-rose-200 bg-rose-50/40 rounded-lg" data-testid="wf-plan-disclaimer">
        <div className="text-xs font-bold uppercase tracking-widest text-rose-900 mb-1 flex items-center gap-1">
          <AlertCircle size={12} /> Disclaimer — please read to client
        </div>
        <ul className="text-[11px] text-slate-700 list-disc pl-5 space-y-0.5">
          <li>Missed instalments incur a <b>${fmt(missedFee)}</b> fee each, and interest continues to accrue at <b>{Number(interestPct || 0).toFixed(1)}% p.a.</b> on the outstanding balance.</li>
          <li>Two or more consecutive missed instalments will terminate this plan and the full remaining balance will become immediately payable, and may be referred to the New Zealand Disputes Tribunal.</li>
          <li>The balance can be settled in full at any time without any early-repayment penalty.</li>
          <li>Any change to the schedule must be agreed in writing by both parties.</li>
        </ul>
        <label className="mt-2 flex items-center gap-2 text-xs text-slate-800">
          <input type="checkbox" checked={disclaimerAck} onChange={(e) => setDisclaimerAck(e.target.checked)}
            data-testid="wf-plan-disclaimer-tick"
            className="w-4 h-4 accent-rose-600" />
          <span>Client has been read the disclaimer and agreed to the terms above.</span>
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!canContinue}
            data-testid="wf-plan-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue to Letter Preview
          </button>
        </div>
      </div>
    </div>
  );
}
